import { unifiedApiKeys } from '@/services/keys';
import { openApiFetch } from '@/lib/openapi-fetch';
import type { SignalsSignalFetchResponse } from '@/types/mercury-data-types';
import { useQuery } from '@tanstack/react-query';
import { findInObject } from '@/utils/findInObject';
import { getNormalizedSignalCollector } from '@/client/features/signals/services/signals';
import { Event } from '@/types/mercury-data-types/events';

const BASE_PATHS = {
  STRUCTURED: 'data.structured',
  CATALOG: 'catalog',
} as const;

export const EVENT_PATHS = {
  ID: ['signal_id'],
  TITLE: [`${BASE_PATHS.STRUCTURED}.title`],
  SUMMARY: [`${BASE_PATHS.STRUCTURED}.summary`],
  TIMESTAMP: [`${BASE_PATHS.STRUCTURED}.timestamp`],
  CENTROID_EVENT_TIMESTAMP: ['centroid_event_timestamp'],
  FIRST_SEEN: [`${BASE_PATHS.STRUCTURED}.firstSeen`],
  LAST_SEEN: [`${BASE_PATHS.STRUCTURED}.lastSeen`],
  CATALOG: [`${BASE_PATHS.CATALOG}`],
  CATEGORY: [`${BASE_PATHS.CATALOG}.category`],
  ARTICLES: ['articles'],
  INDEX: ['index'],
};

export const normalizeEvent = (event: Event) => ({
  id: findInObject(event, EVENT_PATHS.ID),
  title: findInObject(event, EVENT_PATHS.TITLE),
  summary: findInObject(event, EVENT_PATHS.SUMMARY),
  firstSeen: findInObject(event, EVENT_PATHS.TIMESTAMP),
  lastSeen: findInObject(event, EVENT_PATHS.TIMESTAMP),
  timestamp: findInObject(event, EVENT_PATHS.TIMESTAMP),
  centroidEventTimestamp: findInObject(event, EVENT_PATHS.CENTROID_EVENT_TIMESTAMP),
  catalog: findInObject(event, EVENT_PATHS.CATALOG),
  category: findInObject(event, EVENT_PATHS.CATEGORY),
  collector: getNormalizedSignalCollector(findInObject(event, EVENT_PATHS.INDEX)),
  articles: findInObject(event, EVENT_PATHS.ARTICLES),
  totalArticles: findInObject(event, EVENT_PATHS.ARTICLES, [], (articles) => articles.length),
  totalSources: findInObject(
    event,
    EVENT_PATHS.ARTICLES,
    [],
    (sources) => Array.from(new Set(sources.flatMap((value: any) => value.source))).length
  ),
  raw: event,
  index: findInObject(event, EVENT_PATHS.INDEX),
});

export const eventQuery = (id: string) => ({
  queryKey: unifiedApiKeys.ticket('/signals', id),
  queryFn: () =>
    openApiFetch('/signals/{resource_id}', {
      params: {
        path: { resource_id: id },
      },
      headers: { source: 'entity' },
    }),
  select: (raw: any) => {
    const data = raw as SignalsSignalFetchResponse;

    return normalizeEvent(data as any);
  },
});

export type NormalizedEvent = ReturnType<typeof normalizeEvent>;

export const useEventEntity = (id: string) => useQuery(eventQuery(id));
